import React, { FC } from 'react';
import Head from 'react-helmet';

import cn from 'classnames';

import { ArrowRight } from '@crehana/icons';
import { CrehanaIso } from '@crehana/icons/logos';
import { useTranslation } from '@crehana/i18n';

import type { BreadcrumpItem } from './types';

interface BreadcrumpSEOProps {
  /**
   * Optional dynamic title.
   */
  title?: string;
  /**
   * Optional dynamic description.
   */
  description?: string;
  /**
   * List of items containing url, name, optional image and optional custom click function.
   */
  breadcrumbs: BreadcrumpItem[];
  /**
   * If true, it will include breadcrumbs script but exclude it from the interface.
   * Avoid using it as possible, since Google recommends:
   * If the JSON-LD markup describes a performer, the HTML body should describe that same performer.
   */
  hidden?: boolean;
  isDark?: boolean;
}

const BreadcrumpSEO: FC<BreadcrumpSEOProps> = ({
  breadcrumbs,
  title,
  description,
  hidden = false,
  isDark = false,
}) => {
  const { t } = useTranslation();
  const BASE_URL =
    typeof window !== 'undefined' ? `${window.location.origin}/` : '';

  const getOnClick = (bc: BreadcrumpItem, last: boolean) => {
    if (!last) {
      if (bc.onClick) {
        return bc.onClick;
      }
      return () => window.location.assign(`${BASE_URL}${bc.url}`);
    }
    return () => {};
  };

  /**
   * A breadcrumb trail on a page indicates the page's position in the site hierarchy.
   * Google Search uses breadcrumb markup to categorize the information
   * from the page in search results.
   */
  const beadcrumbList = {
    '@context': 'http://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        item: {
          '@id': BASE_URL,
          name: t('START'),
          image: undefined as unknown as string,
        },
      },
    ],
  };

  for (let i = 0; i < breadcrumbs.length; i += 1) {
    beadcrumbList.itemListElement.push({
      '@type': 'ListItem',
      position: i + 2,
      item: {
        '@id': `${BASE_URL}${breadcrumbs[i]!.url}`,
        name: breadcrumbs[i]!.name,
        image: breadcrumbs[i]!.image!,
      },
    });
  }

  return (
    <div aria-roledescription={description}>
      <Head>
        <script type="application/ld+json">
          {JSON.stringify(beadcrumbList)}
        </script>
        {title && <title>{title}</title>}
      </Head>
      {!hidden && (
        <div className="tw-font-pop flex items-center flex-wrap sm:flex-nowrap" style={{
          fontSize: 14
        }}>
          <button
            type="button"
            aria-label={t('START')}
            className="mb-12 sm:mb-0"
            style={{
              maxHeight: 16,
            }}
            onClick={() => {
              window.location.assign(BASE_URL);
            }}
          >
            <CrehanaIso
              size={16}
              className={cn(
                'cursor-pointer hover:text-secondary-light min-w-16',
                isDark ? 'text-primary-light' : 'text-primary-main',
              )}
            />
          </button>

          {breadcrumbs.map((bc, index) => {
            const isLastItem = index === breadcrumbs.length - 1;
            const urlWithTrailingSlash = bc.url.endsWith('/')
              ? bc.url
              : `${bc.url}/`;

            return [
              <ArrowRight
                key={index.toString()}
                size={16}
                ignoreColor
                className="fill-current text-primary-main mx-8 min-w-24 mb-12 sm:mb-0"
                aria-hidden
              />,
              <a
                key={bc.name.concat(index.toString())}
                className={cn(
                  'flex-shrink-0 mb-12 sm:mb-0 font-normal sm:font-extrabold',
                  isLastItem
                    ? 'cursor-default text-base-lighter'
                    : 'cursor-pointer text-primary-main hover:text-primary-dark',
                )}
                onClick={ev => {
                  ev.preventDefault();
                  ev.stopPropagation();

                  getOnClick(bc, isLastItem)();
                }}
                href={urlWithTrailingSlash}
                rel="noopener noreferrer"
              >
                {t(bc.name, { defaultValue: bc.name })}
              </a>,
            ];
          })}
        </div>
      )}
    </div>
  );
};

export default BreadcrumpSEO;
